const MENU_VIEWPORT_GAP = 16;

class ArticleFilterDropdown extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("[data-article-filter-button]");
    this.menu = this.querySelector("[data-article-filter-menu]");
    this.selectedValue = this.querySelector(
      "[data-article-filter-selected-value]",
    );

    if (
      !(this.button instanceof HTMLButtonElement) ||
      !(this.menu instanceof HTMLElement)
    )
      return;

    this.addEventListener("click", this.handleClick);
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleKeydown);
    window.addEventListener("resize", this.handleViewportResize);
    window.visualViewport?.addEventListener(
      "resize",
      this.handleViewportResize,
    );
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("resize", this.handleViewportResize);
    window.visualViewport?.removeEventListener(
      "resize",
      this.handleViewportResize,
    );
  }

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest("[data-article-filter-button]")) {
      this.toggle();
      return;
    }

    const item = event.target.closest(".dropdown-item");
    if (!item || !this.contains(item)) return;

    const itemText = item.textContent?.trim();
    if (this.selectedValue && itemText)
      this.selectedValue.textContent = itemText;
    this.close();
  };

  handleDocumentClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target))
      this.close();
  };

  handleKeydown = (event) => {
    if (event.key !== "Escape" || !this.isOpen) return;

    this.close();
    this.button?.focus();
  };

  handleViewportResize = () => {
    if (this.isOpen && this.autoScrollEnabled) this.updateMenuMaxHeight();
  };

  get isOpen() {
    return this.button?.getAttribute("aria-expanded") === "true";
  }

  get autoScrollEnabled() {
    return this.dataset.autoScroll === "true";
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.button || !this.menu) return;

    this.menu.classList.remove("tw:hidden");
    this.menu.classList.add("tw:block");
    this.button.setAttribute("aria-expanded", "true");

    if (!this.autoScrollEnabled) return;

    this.updateMenuMaxHeight();
    requestAnimationFrame(() => this.scrollToStart());
  }

  close() {
    if (!this.button || !this.menu || !this.isOpen) return;

    this.menu.classList.remove("tw:block");
    this.menu.classList.add("tw:hidden");
    this.button.setAttribute("aria-expanded", "false");
  }

  updateMenuMaxHeight() {
    if (!this.button || !this.menu) return;

    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const availableHeight =
      viewportHeight -
      this.getHeaderOffset() -
      this.button.offsetHeight -
      MENU_VIEWPORT_GAP;

    this.style.setProperty(
      "--article-filter-menu-max-height",
      `${Math.max(0, availableHeight)}px`,
    );
  }

  scrollToStart() {
    const top =
      window.scrollY +
      this.getBoundingClientRect().top -
      this.getHeaderOffset() -
      MENU_VIEWPORT_GAP / 2;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: reduceMotion ? "instant" : "smooth",
    });
  }

  getHeaderOffset() {
    const header = document.querySelector("#header-component[sticky]");
    if (!(header instanceof HTMLElement)) return 0;

    return header.getBoundingClientRect().height;
  }
}

if (!customElements.get("article-filter-dropdown")) {
  customElements.define("article-filter-dropdown", ArticleFilterDropdown);
}
